// @flow

import React from 'react';
import {
  Box,
  Divider,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import useIntl from '../../hooks/useIntl';
import messages from './messages';
import type { FunnelGroup, HoldStage } from './types';

// Groups are separated by whitespace, an uppercase label, and a hairline rule —
// no fills or boxes.
const groupLabelSx = {
  color: 'text.secondary',
  display: 'block',
  letterSpacing: '0.07em',
  mb: 0.75,
  textTransform: 'uppercase',
};

// One funnel row: the stage label on the left, its count right-aligned. The
// selected row carries the app's standard selected fill (primary.lighter) — the
// same pale blue Fortress uses for a selected list item — with no border or bar.
const rowSx = (selected: boolean): Object => ({
  alignItems: 'center',
  background: 'transparent',
  border: 'none',
  borderRadius: 1,
  color: 'inherit',
  cursor: 'pointer',
  display: 'grid',
  font: 'inherit',
  gap: 2,
  gridTemplateColumns: '1fr auto',
  px: 1,
  py: 1,
  textAlign: 'left',
  width: '100%',
  '&:hover': { backgroundColor: 'action.hover' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'text.secondary' },
  ...(selected ? { backgroundColor: 'primary.lighter' } : {}),
});

type FunnelRowProps = {
  label: string,
  count: number,
  countText: string,
  ariaLabel: string,
  selected: boolean,
  onSelect: () => void,
  testId: string,
};

// A stage at zero is not special-cased structurally — it renders like every
// other row, its count simply shown in a quieter ink so an empty stage reads as
// intentional rather than broken.
const FunnelRow = ({
  label,
  count,
  countText,
  ariaLabel,
  selected,
  onSelect,
  testId,
}: FunnelRowProps) => {
  const handleKeyDown = (event: SyntheticKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={ariaLabel}
      data-testid={testId}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      sx={rowSx(selected)}
    >
      <Typography
        variant="bodyMedium"
        sx={{ color: 'text.primary', whiteSpace: 'nowrap' }}
      >
        {label}
      </Typography>
      <Typography
        variant="bodyMedium"
        sx={{
          color: count === 0 ? 'text.disabled' : 'text.primary',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 500,
          textAlign: 'right',
        }}
      >
        {countText}
      </Typography>
    </Box>
  );
};

type Props = {
  groups: Array<FunnelGroup>,
  holdStages: Array<HoldStage>,
  selectedStageKey: string,
  onSelectStage: (stageKey: string) => void,
};

const CertificationFunnel = ({
  groups,
  holdStages,
  selectedStageKey,
  onSelectStage,
}: Props) => {
  const intl = useIntl();
  const numeral = (count: number): string => intl.formatNumber(count);
  const ariaFor = (label: string, count: number): string =>
    intl.formatMessage(messages.stageAriaLabel, { label, count });

  const handleHoldKeyDown = (
    event: SyntheticKeyboardEvent<HTMLDivElement>,
    stageKey: string,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelectStage(stageKey);
    }
  };

  return (
    <Box
      role="group"
      aria-label={intl.formatMessage(messages.funnelRegionLabel)}
      data-testid="certification-funnel"
      sx={{ width: '100%' }}
    >
      {/* Groups: separated by whitespace, an uppercase label, and a hairline
          rule — no fills or boxes. */}
      <Stack spacing={4}>
        {groups.map((group) => (
          <Box key={group.key} data-testid={`funnel-group-${group.key}`}>
            <Typography variant="labelSmall" sx={groupLabelSx}>
              {group.label}
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Stack spacing={0.5}>
              {group.stages.map((stage) => (
                <FunnelRow
                  key={stage.key}
                  label={stage.label}
                  count={stage.count}
                  countText={numeral(stage.count)}
                  ariaLabel={ariaFor(stage.label, stage.count)}
                  selected={stage.key === selectedStageKey}
                  onSelect={() => onSelectStage(stage.key)}
                  testId={`funnel-stage-${stage.key}`}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>

      {/* Beneath the funnel: each hold stage names itself (it is one status, not
          an invented category) as a quiet uppercase section label above a plain
          count row, with extra whitespace so it reads as deliberate rather than
          orphaned. It does not take part in the flow. */}
      {holdStages.length > 0 && (
        <Stack spacing={4} sx={{ mt: 7 }} data-testid="funnel-hold">
          {holdStages.map((stage) => {
            const selected = stage.key === selectedStageKey;
            return (
              <Box key={stage.key}>
                <Typography variant="labelSmall" sx={groupLabelSx}>
                  {stage.label}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Box
                  role="button"
                  tabIndex={0}
                  aria-pressed={selected}
                  aria-label={ariaFor(stage.label, stage.count)}
                  data-testid={`funnel-hold-${stage.key}`}
                  onClick={() => onSelectStage(stage.key)}
                  onKeyDown={(event) => handleHoldKeyDown(event, stage.key)}
                  sx={{
                    alignItems: 'center',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 1,
                    color: 'inherit',
                    cursor: 'pointer',
                    display: 'flex',
                    font: 'inherit',
                    px: 1,
                    py: 1,
                    textAlign: 'left',
                    width: '100%',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'text.secondary',
                    },
                    ...(selected ? { backgroundColor: 'primary.lighter' } : {}),
                  }}
                >
                  <Typography
                    variant="titleSmall"
                    sx={{
                      color: 'text.primary',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {numeral(stage.count)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default CertificationFunnel;
